import { useMemo } from "react";
import { useRecoilValue } from "recoil";

import { useConfig } from "src/hooks/config";
import Money from "src/common/components/money";
import { getBetCalculations } from "calculation";
import upperDataStore from "src/stores/upperData";
import selectionsStore from "src/stores/selections";
import { useTranslation } from "src/hooks/translation";
import { BetCalculationsEnum } from "src/constants/enums/betCalculations";
import mainStore from "src/stores/main";
import { BetTypeEnum } from "src/constants/enums/betType";
import betBuilderStore from "src/stores/betBuilder";
import { getEffectiveSelectionsForCalc, hasBetBuilderSelections } from "src/common/helpers/betBuilder";

interface IProps {
	stake: number;
}

const BetDetailsPossibleWin = ({ stake }: IProps) => {
	const t = useTranslation();
	const config = useConfig();
	const { betType, systemCombinationCount, freeBets } = useRecoilValue(mainStore);
	const { selections } = useRecoilValue(selectionsStore);
	const { partnerConfig, currencyConfig } = useRecoilValue(upperDataStore);
	const { groups: betBuilderGroups } = useRecoilValue(betBuilderStore);
	const selectedFreeBet = freeBets.find(item => item.selected);

	const { possibleWin } = useMemo(() => {
		// Bet builder cards collapse to a single combined odd per game. We
		// synthesize one pseudo-leg per BB card and run the regular calc
		// pipeline so free bet, bonus rules, and tax all apply correctly.
		const hasBb = hasBetBuilderSelections(selections);
		const effectiveSelections = hasBb
			? getEffectiveSelectionsForCalc(selections, betBuilderGroups)
			: selections;

		if (effectiveSelections === null) return { possibleWin: 0 };

		return getBetCalculations([BetCalculationsEnum.PossibleWin], {
			config,
			partnerConfig,
			// BB always settles as a Multiple shape (N legs × combined odds);
			// force the Multiple formula so a UI-selected Single/System type
			// doesn't pick the wrong handler when BB is present.
			betType: hasBb ? BetTypeEnum.Multiple : betType,
			stake: +stake,
			events: effectiveSelections,
			systemCombinationCount,
			selectedFreeBet,
			currencyConfig,
		});
	}, [config, partnerConfig, currencyConfig, stake, betType, selections, systemCombinationCount, selectedFreeBet, betBuilderGroups]);

	if (!possibleWin) return null;

	return (
		<div className="B-betslip-bet-details-info-row">
			<p className="B-info-row-title">
				{t(betType === BetTypeEnum.System ? 'Max Possible win:' : 'Possible win:')}
			</p>
			<p className="B-info-row-number">
				<Money amount={possibleWin} />
			</p>
		</div>
	);
};

export default BetDetailsPossibleWin;
